// ProfilePage.js
import React, { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Divider,
  Button,
  TextField,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import api from "../api/axios";

const ProfilePage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [profilePic, setProfilePic] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [needsPassword, setNeedsPassword] = useState(false);

  const {
    register,
    setValue,
    handleSubmit,
    watch,
  } = useForm();

  const pincode = watch("pincode");
  const password = watch("password");

  const showSnack = (msg, severity = "success") =>
    setSnack({ open: true, message: msg, severity });

  /* ---------------- LOAD USER ---------------- */
  useEffect(() => {
    const savedUser = JSON.parse(localStorage.getItem("user") || "{}");

    if (!savedUser.username) {
      navigate("/login");
      return;
    }

    api
      .get(`/users/profile/${savedUser.username}`)
      .then((res) => {
        const u = res.data.user;

        localStorage.setItem("user", JSON.stringify(u));

        setNeedsPassword(!u.hasPassword); // 🔑 AMAZON LOGIC
        // setNeedsPassword(!u.password); // 🔑 AMAZON LOGIC
        setValue("firstName", u.firstName || "");
        setValue("lastName", u.lastName || "");
        setValue("gender", u.gender || "");
        setValue("mobile", u.mobile || "");
        setValue("email", u.email || u.username);

        if (u.profilePic) setProfilePic(u.profilePic);

        if (u.addresses?.length) {
          const a = u.addresses.find((x) => x.isDefault) || u.addresses[0];
          setValue("addressLine1", a.addressLine1 || "");
          setValue("addressLine2", a.addressLine2 || "");
          setValue("pincode", a.pincode || "");
          setCity(a.city || "");
          setState(a.state || "");
          setValue("addressType", a.addressType || "Home");
          setValue("otherType", a.otherType || "");
        }
      })
      .catch(() => navigate("/login"));
  }, [navigate, setValue]);

  /* ---------------- IMAGE UPLOAD ---------------- */
  const convertToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const base64 = await convertToBase64(file);
    setProfilePic(base64);
  };

  /* ---------------- PINCODE LOOKUP ---------------- */
  const fetchCityState = async (pin) => {
    if (pin.length !== 6) return;
    try {
      const res = await axios.get(`https://api.postalpincode.in/pincode/${pin}`);
      if (res.data[0].Status === "Success") {
        const info = res.data[0].PostOffice[0];
        setCity(info.District);
        setState(info.State);
      } else showSnack("Invalid Pincode", "error");
    } catch {
      showSnack("Couldn't fetch pincode info", "error");
    }
  };

  useEffect(() => {
    if (pincode?.length === 6) fetchCityState(pincode);
  }, [pincode]);

  /* ---------------- SUBMIT ---------------- */
 const onSubmit = async (data) => {
  try {
    const existingUser = JSON.parse(localStorage.getItem("user"));

    /* 🔐 STEP 1: SET PASSWORD (FIRST TIME ONLY) */
    if (needsPassword) {
      if (!data.password || data.password.length < 6) {
        showSnack("Password must be at least 6 characters", "error");
        return;
      }

      if (data.password !== data.confirmPassword) {
        showSnack("Passwords do not match", "error");
        return;
      }

      const passRes = await api.post("/auth/set-password", {
        username: existingUser.username,
        password: data.password,
      });

      /* ✅ SAVE TOKEN IMMEDIATELY */
      localStorage.setItem("token", passRes.data.token);
      localStorage.setItem("user", JSON.stringify(passRes.data.user));
    }

    /* 🧾 STEP 2: SAVE PROFILE */
    const payload = {
      username: existingUser.username,
      firstName: data.firstName,
      lastName: data.lastName,
      gender: data.gender,
      email: data.email,
      mobile: data.mobile,
      profilePic,
      addresses: [
        {
          addressLine1: data.addressLine1,
          addressLine2: data.addressLine2,
          city,
          state,
          country: "India",
          pincode: data.pincode,
          addressType: data.addressType,
          otherType: data.otherType || "",
          isDefault: true,
        },
      ],
    };

    const res = await api.put("/users/profile", payload);

    /* 🔄 UPDATE USER AFTER PROFILE SAVE */
    localStorage.setItem("user", JSON.stringify(res.data.user));

    showSnack("Profile updated successfully");
    setTimeout(() => navigate("/"), 800);

  } catch (err) {
    console.error(err);
    showSnack("Failed to update profile", "error");
  }
};


  const handleLogout = () => {
    localStorage.removeItem("user");
    navigate("/login");
  };

   /*Used to create TextField with always shrunk label*/
    const ShrinkTextField = (props) => (
    <TextField
      {...props}
      slotProps={{
        inputLabel: { shrink: true }
      }}
    />
  )

  /* ---------------- UI ---------------- */
  return (
    <Box sx={{ minHeight: "80vh", display: "flex", justifyContent: "center", pt: 4 }}>
      <Paper elevation={4} sx={{ width: "100%", maxWidth: 650, p: 4, borderRadius: 3 }}>
        <Box sx={{ textAlign: "center" }}>
          <Avatar
            src={profilePic || ""}
            sx={{ width: 110, height: 110, mx: "auto", cursor: "pointer", bgcolor: "#494948ff" }}
            onClick={() => fileInputRef.current.click()}
          >
            {!profilePic && "U"}
          </Avatar>

          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            hidden
            onChange={handleImageUpload}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <ShrinkTextField label="First Name" fullWidth {...register("firstName", { required: true })} />
            <ShrinkTextField label="Last Name" fullWidth {...register("lastName")} />

            <FormControl fullWidth>
              <InputLabel>Gender</InputLabel>
              <Select defaultValue="" {...register("gender")}>
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>

            <ShrinkTextField label="Email" fullWidth disabled {...register("email")} />
            <ShrinkTextField label="Mobile Number" fullWidth {...register("mobile")} />
          </Box>

          {/* 🔑 PASSWORD SECTION (ONLY IF NEEDED) */}
          {needsPassword && (
            <>
              <Divider sx={{ my: 4 }} />
              <Typography variant="h6" fontWeight="bold">
                Set Password
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
                <TextField
                  label="Password"
                  type="password"
                  fullWidth
                  {...register("password")}
                />
                <TextField
                  label="Confirm Password"
                  type="password"
                  fullWidth
                  {...register("confirmPassword")}
                />
              </Box>
            </>
          )}

          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" fontWeight="bold">
            Address
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <ShrinkTextField label="Address Line 1" fullWidth {...register("addressLine1", { required: true })} />
            <ShrinkTextField label="Address Line 2" fullWidth {...register("addressLine2")} />
            <ShrinkTextField label="Pincode" fullWidth {...register("pincode")} />
            <ShrinkTextField label="City" fullWidth value={city} disabled />
            <ShrinkTextField label="State" fullWidth value={state} disabled />

            <FormControl fullWidth>
              <InputLabel>Address Type</InputLabel>
              <Select defaultValue="Home" {...register("addressType")}>
                <MenuItem value="Home">Home</MenuItem>
                <MenuItem value="Work">Work</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>

            {watch("addressType") === "Other" && (
              <ShrinkTextField label="Specify Type" fullWidth {...register("otherType")} />
            )}
          </Box>

          <Divider sx={{ my: 4 }} />

          <Button type="submit" fullWidth variant="contained" sx={{ bgcolor: "#494948ff" }}>
            Save Profile
          </Button>
        </form>

        <Button fullWidth variant="outlined" sx={{ mt: 2 }} onClick={handleLogout}>
          Logout
        </Button>
      </Paper>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.severity} variant="filled">
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfilePage;
